import type {
	NavigationKlausurDTO,
	NavigationSachverhaltDTO,
} from "@/types/klausurEditor.types";
import type {
	KorrekturStudentKlausurDTO,
	MusterloesungDTO,
} from "@/features/korrektur/korrektur.types";
import { createContext, useContext, useMemo } from "react";
import { KlausurCoreProvider, useKlausurCore } from "./klausurCoreProvider";
import { KlausurCrudProvider, useKlausurCrud } from "./klausurCrudProvider";

/**
 * Combined context value for klausur editing.
 * Merges core data loading and CRUD operations into a single interface.
 * This maintains backward compatibility with existing consumers.
 */
interface KlausurEditingContextValue {
	/** Selected klausur id */
	klausurId: string;
	/** Basic klausur metadata */
	klausurMeta: { id: string; title: string } | null;
	/** Navigation data (aufgabenstellungen and sachverhalte) for sidebar */
	navigationKlausur: NavigationKlausurDTO | null;
	/** Full klausur structure for editor rendering */
	data: KorrekturStudentKlausurDTO | null;
	/** Loading state for detailed klausur */
	isLoading: boolean;
	/** Error state for detailed klausur */
	error: string | null;
	/** Load klausur metadata and navigation structure */
	loadKlausurMeta: (klausurId: string) => Promise<void>;
	/** Load detailed klausur structure for editor */
	loadKlausurData: (klausurId: string) => Promise<void>;
	/** Reload both meta and data from backend */
	reloadKlausur: () => Promise<void>;
	/** Open a modal to create a new Aufgabenstellung */
	openCreateAufgabenstellungModal: () => void;
	/** Open a modal to create a new Sachverhalt for an Aufgabenstellung */
	openCreateSachverhaltModal: (aufgabenstellungId: string) => void;
	/** Persist reordered sachverhalte indexes for a given aufgabenstellung */
	updateSachverhalteIndexes: (
		aufgabenstellungId: string,
		newList: NavigationSachverhaltDTO[],
	) => Promise<void>;
	/** Create a new musterloesung for a sachverhalt; returns created id */
	createMusterloesung: (
		sachverhaltId: string,
		title?: string,
	) => Promise<string>;
	/** Update a musterloesung's title */
	updateMusterloesungTitle: (
		musterloesungId: string,
		title: string,
	) => Promise<void>;
	/** Update an aufgabenstellung's title */
	updateAufgabenstellungTitle: (
		aufgabenstellungId: string,
		title: string,
	) => Promise<void>;
	/** Update a sachverhalt's title */
	updateSachverhaltTitle: (
		sachverhaltId: string,
		title: string,
	) => Promise<void>;
	/** Delete an aufgabenstellung */
	deleteAufgabenstellung: (aufgabenstellungId: string) => Promise<void>;
	/** Delete a sachverhalt under a given aufgabenstellung */
	deleteSachverhalt: (
		aufgabenstellungId: string,
		sachverhaltId: string,
	) => Promise<void>;
	/** Update a korrekturbogen item's max points */
	updateKorrekturbogenItemMaxPoints: (
		korrekturbogenItemId: string,
		maxPoints: number,
	) => Promise<void>;
	/** Update a korrekturbogen item's text */
	updateKorrekturbogenItemText: (
		korrekturbogenItemId: string,
		text: string,
	) => Promise<void>;
	/** Create a new korrekturbogen item within a musterloesung; returns created id */
	createKorrekturbogenItem: (
		musterloesung: MusterloesungDTO,
		title?: string,
	) => Promise<string>;
	/** UI helpers for in-flight creations */
	isCreatingMusterloesung: (sachverhaltId: string) => boolean;
	isCreatingItem: (musterloesungId: string) => boolean;
}

/**
 * Context for klausur editing.
 * Composes core data loading and CRUD operations.
 */
const KlausurEditingContext = createContext<
	KlausurEditingContextValue | undefined
>(undefined);

interface KlausurEditingProviderProps {
	children: React.ReactNode;
	klausurId: string;
}

/**
 * Main provider for klausur editing functionality.
 * Composes KlausurCoreProvider and KlausurCrudProvider to provide
 * a complete interface for managing klausur data and operations.
 *
 * Architecture:
 * - KlausurCoreProvider: Handles data loading, state, and reloading
 * - KlausurCrudProvider: Handles create/update/delete operations
 * - KlausurEditingProvider: Combines both into a single interface
 *
 * This layered approach improves code organization and maintainability
 * while preserving backward compatibility for existing consumers.
 */
export function KlausurEditingProvider({
	children,
	klausurId,
}: KlausurEditingProviderProps) {
	return (
		<KlausurCoreProvider klausurId={klausurId}>
			<KlausurCrudProvider>
				<KlausurEditingBridge>{children}</KlausurEditingBridge>
			</KlausurCrudProvider>
		</KlausurCoreProvider>
	);
}

/**
 * Bridge component that combines core and CRUD contexts into
 * the unified KlausurEditingContext for backward compatibility.
 */
function KlausurEditingBridge({ children }: { children: React.ReactNode }) {
	const core = useKlausurCore();
	const crud = useKlausurCrud();

	// Combine both contexts into the unified interface
	const value = useMemo(
		() => ({
			// Core data and loading
			klausurId: core.klausurId,
			klausurMeta: core.klausurMeta,
			navigationKlausur: core.navigationKlausur,
			data: core.data,
			isLoading: core.isLoading,
			error: core.error,
			loadKlausurMeta: core.loadKlausurMeta,
			loadKlausurData: core.loadKlausurData,
			reloadKlausur: core.reloadKlausur,
			// CRUD operations
			openCreateAufgabenstellungModal: crud.openCreateAufgabenstellungModal,
			openCreateSachverhaltModal: crud.openCreateSachverhaltModal,
			updateSachverhalteIndexes: crud.updateSachverhalteIndexes,
			createMusterloesung: crud.createMusterloesung,
			updateMusterloesungTitle: crud.updateMusterloesungTitle,
			updateAufgabenstellungTitle: crud.updateAufgabenstellungTitle,
			updateSachverhaltTitle: crud.updateSachverhaltTitle,
			deleteAufgabenstellung: crud.deleteAufgabenstellung,
			deleteSachverhalt: crud.deleteSachverhalt,
			updateKorrekturbogenItemMaxPoints: crud.updateKorrekturbogenItemMaxPoints,
			updateKorrekturbogenItemText: crud.updateKorrekturbogenItemText,
			createKorrekturbogenItem: crud.createKorrekturbogenItem,
			isCreatingMusterloesung: crud.isCreatingMusterloesung,
			isCreatingItem: crud.isCreatingItem,
		}),
		[core, crud],
	);

	return (
		<KlausurEditingContext.Provider value={value}>
			{children}
		</KlausurEditingContext.Provider>
	);
}

/**
 * Hook to access the combined klausur editing context.
 * Must be used within KlausurEditingProvider.
 *
 * For new code, consider using useKlausurCore() and useKlausurCrud()
 * directly for better separation of concerns.
 */
export function useKlausurEditing() {
	const ctx = useContext(KlausurEditingContext);
	if (!ctx)
		throw new Error(
			"useKlausurEditing must be used within KlausurEditingProvider",
		);
	return ctx;
}
